import React, { useState, useMemo, useEffect } from 'react';
import { Briefcase } from 'lucide-react';
import type { Technician, Assignment } from '@shared/types';
import { getApiUrl } from '@/config';
import { parseISO } from 'date-fns';
import { EditAssignmentModal } from '../components/Assignment/EditAssignmentModal';
import { AssignmentFilters } from '../components/Assignment/AssignmentFilters';
import type { SortField } from '../components/Assignment/AssignmentFilters';
import { AssignmentList } from '../components/Assignment/AssignmentList';
import { AssignmentKanban } from '../components/Assignment/AssignmentKanban';
import { Pagination } from '@shared/components/UI/Pagination';

interface AssignmentsPageProps {
    technicians: Technician[];
    onUpdateTechnician: (techId: string, updates: Partial<Technician>) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
}

export const AssignmentsPage: React.FC<AssignmentsPageProps> = ({ technicians, onUpdateTechnician, onStartWorksite, onFinishWorksite }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortField>('date');
    const [selectedMonth, setSelectedMonth] = useState<string>(new Date().getMonth().toString());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [editingAssignment, setEditingAssignment] = useState<{ assignment: Assignment; techIds: string[] } | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Get all unique assignments with their assigned technicians
    const allAssignments = useMemo(() => {
        const assignments: Array<{ assignment: Assignment; technicians: Technician[] }> = [];
        const assignmentMap = new Map<string, Technician[]>();

        // Include all technicians (even inactive) for historical assignment viewing
        // But maybe we want to visual flag them?
        technicians.forEach(tech => {
            tech.assignments.forEach(assignment => {
                if (!assignmentMap.has(assignment.id)) {
                    assignmentMap.set(assignment.id, []);
                }
                assignmentMap.get(assignment.id)!.push(tech);
            });
        });

        assignmentMap.forEach((techs, assignmentId) => {
            const assignment = techs[0].assignments.find(a => a.id === assignmentId)!;
            assignments.push({ assignment, technicians: techs });
        });

        return assignments;
    }, [technicians]);

    // Active technicians for dropdowns/modals
    // const activeTechnicians = useMemo(() => technicians.filter(t => t.active !== false), [technicians]);


    // Filter and sort assignments
    const filteredAndSortedAssignments = useMemo(() => {
        let filtered = allAssignments;

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(({ assignment, technicians: assignedTechs }) => {
                const matchesClient = assignment.clientName.toLowerCase().includes(query);
                const matchesCity = assignment.city?.toLowerCase().includes(query);
                const matchesTech = assignedTechs.some(t => t.name.toLowerCase().includes(query));
                const matchesId = assignment.worksiteId?.toLowerCase().includes(query) || assignment.id.toLowerCase().includes(query);
                const matchesPhone = assignment.contactPhone?.toLowerCase().replace(/\s/g, '').includes(query.replace(/\s/g, '')) || false;

                return matchesClient || matchesCity || matchesTech || matchesId || matchesPhone;
            });
        }

        // Month Filter
        if (selectedMonth !== 'all' && !selectedDate) {
            const monthIndex = parseInt(selectedMonth);
            filtered = filtered.filter(({ assignment }) => {
                const start = parseISO(assignment.startDate);
                const end = parseISO(assignment.endDate);

                const monthStart = new Date(selectedYear, monthIndex, 1);
                const monthEnd = new Date(selectedYear, monthIndex + 1, 0); // Last day of month

                return (
                    (start <= monthEnd && end >= monthStart)
                );
            });
        }

        // Date Filter
        if (selectedDate) {
            filtered = filtered.filter(({ assignment }) => {
                return assignment.startDate <= selectedDate && assignment.endDate >= selectedDate;
            });
        }

        // Status Filter
        if (selectedStatus !== 'all') {
            filtered = filtered.filter(({ assignment }) => {
                const status = assignment.status || 'pending';
                return status === selectedStatus;
            });
        }

        // Sort
        return filtered.sort((a, b) => {
            switch (sortBy) {
                case 'client':
                    return a.assignment.clientName.localeCompare(b.assignment.clientName);
                case 'city':
                    return (a.assignment.city || '').localeCompare(b.assignment.city || '');
                case 'date':
                    return a.assignment.startDate.localeCompare(b.assignment.startDate);
                default:
                    return 0;
            }
        });
    }, [allAssignments, searchQuery, sortBy, selectedMonth, selectedDate, selectedStatus, selectedYear]);

    // Reset page when filters change
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrentPage(1);
    }, [searchQuery, selectedMonth, selectedDate, selectedStatus, selectedYear, sortBy]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredAndSortedAssignments.length / ITEMS_PER_PAGE);
    const paginatedAssignments = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredAndSortedAssignments.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredAndSortedAssignments, currentPage]);


    // Handle edit
    const handleEditAssignment = (assignment: Assignment, techIds: string[]) => {
        setEditingAssignment({ assignment, techIds });
    };

    // Handle save edited assignment
    const handleSaveEdit = async (updatedAssignment: Assignment, newTechIds: string[]) => {
        if (!editingAssignment) return;

        const oldTechIds = editingAssignment.techIds;
        const assignmentId = updatedAssignment.id;

        // 1. Update Local State (Optimistic UI)
        // Remove from old technicians not in new list
        oldTechIds.forEach(techId => {
            if (!newTechIds.includes(techId)) {
                const tech = technicians.find(t => t.id === techId);
                if (tech) {
                    const newAssignments = tech.assignments.filter(a => a.id !== assignmentId);
                    onUpdateTechnician(techId, { assignments: newAssignments });
                }
            }
        });

        // Update or add to new technicians
        newTechIds.forEach(techId => {
            const tech = technicians.find(t => t.id === techId);
            if (tech) {
                const existingIndex = tech.assignments.findIndex(a => a.id === assignmentId);
                let newAssignments;

                if (existingIndex >= 0) {
                    // Update existing
                    newAssignments = [...tech.assignments];
                    newAssignments[existingIndex] = updatedAssignment;
                } else {
                    // Add new
                    newAssignments = [...tech.assignments, updatedAssignment];
                }

                onUpdateTechnician(techId, { assignments: newAssignments });
            }
        });

        setEditingAssignment(null);

        // 2. Persist to Backend (Canonical Source)
        try {
            const token = localStorage.getItem('token');
            // We use the first tech ID as the primary owner if multiple, or null
            const primaryTechId = newTechIds.length > 0 ? newTechIds[0] : null;

            const payload = {
                ...updatedAssignment,
                technicianId: primaryTechId
            };

            await fetch(getApiUrl(`/api/assignments/${assignmentId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
        } catch (error) {
            console.error('Failed to save assignment changes to backend:', error);
            // Optionally flip back state or show toast
        }
    };

    // Handle delete assignment
    const handleDeleteAssignment = () => {
        if (!editingAssignment) return;

        const assignmentId = editingAssignment.assignment.id;

        // Remove from all technicians
        editingAssignment.techIds.forEach(techId => {
            const tech = technicians.find(t => t.id === techId);
            if (tech) {
                const newAssignments = tech.assignments.filter(a => a.id !== assignmentId);
                onUpdateTechnician(techId, { assignments: newAssignments });
            }
        });

        setEditingAssignment(null);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-main mb-2 flex items-center gap-3">
                    <Briefcase className="text-accent" size={32} />
                    Gestion des Chantiers
                </h1>
                <p className="text-muted">Vue d'ensemble et modification de tous les chantiers</p>
            </div>

            {/* Search and Filters */}
            <AssignmentFilters
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortBy={sortBy}
                setSortBy={setSortBy}
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                viewMode={viewMode}
                setViewMode={setViewMode}
                onExport={() => {
                    const headers = ['ID', 'Type', 'Client', 'Adresse', 'CP', 'Ville', 'Contact Nom', 'Contact Tel', 'Date Debut', 'Heure Debut', 'Date Fin', 'Heure Fin', 'Statut', 'Techniciens', 'Tache', 'Remarque'];
                    const rows = filteredAndSortedAssignments.map(({ assignment, technicians }) => [
                        assignment.worksiteId || assignment.id,
                        assignment.type || 'Maintenance',
                        assignment.clientName,
                        assignment.address || '',
                        assignment.postalCode || '',
                        assignment.city || '',
                        assignment.contactName || '',
                        assignment.contactPhone || '',
                        assignment.startDate,
                        assignment.startTime || '',
                        assignment.endDate,
                        assignment.endTime || '',
                        assignment.status || 'todo',
                        technicians.map(t => t.name).join(', '),
                        assignment.task || '',
                        assignment.remarque || ''
                    ]);

                    const csvContent = [
                        headers.join(','),
                        ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
                    ].join('\n');

                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    const dateStr = new Date().toISOString();
                    const safeDate = dateStr ? dateStr.split('T')[0] : 'unknown-date';
                    link.download = `chantiers_export_${safeDate}.csv`;
                    link.click();
                }}
            />

            {/* Dashboard KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Total */}
                <div className="glass-panel p-3 border-l-4 border-l-gray-500 relative overflow-hidden group">
                    <div className="absolute right-2 top-2 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Briefcase size={24} />
                    </div>
                    <div className="text-muted text-[10px] font-bold uppercase tracking-wider">Total</div>
                    <div className="text-2xl font-bold text-main mt-1">{filteredAndSortedAssignments.length}</div>
                </div>

                {/* En Attente */}
                <div className="glass-panel p-3 border-l-4 border-l-yellow-500 relative overflow-hidden group">
                    <div className="absolute right-2 top-2 opacity-10 group-hover:opacity-20 transition-opacity text-yellow-500">
                        <Briefcase size={24} />
                    </div>
                    <div className="text-yellow-500/80 text-[10px] font-bold uppercase tracking-wider">En Attente</div>
                    <div className="text-2xl font-bold text-yellow-500 mt-1">
                        {filteredAndSortedAssignments.filter(a => a.assignment.status === 'pending').length}
                    </div>
                </div>

                {/* Planifié */}
                <div className="glass-panel p-3 border-l-4 border-l-orange-500 relative overflow-hidden group">
                    <div className="absolute right-2 top-2 opacity-10 group-hover:opacity-20 transition-opacity text-orange-500">
                        <Briefcase size={24} />
                    </div>
                    <div className="text-orange-400/80 text-[10px] font-bold uppercase tracking-wider">Planifié</div>
                    <div className="text-2xl font-bold text-orange-400 mt-1">
                        {filteredAndSortedAssignments.filter(a => a.assignment.status === 'todo').length}
                    </div>
                </div>

                {/* En Cours */}
                <div className="glass-panel p-3 border-l-4 border-l-blue-500 relative overflow-hidden group">
                    <div className="absolute right-2 top-2 opacity-10 group-hover:opacity-20 transition-opacity text-blue-500">
                        <Briefcase size={24} />
                    </div>
                    <div className="text-blue-400/80 text-[10px] font-bold uppercase tracking-wider">En Cours</div>
                    <div className="text-2xl font-bold text-blue-400 mt-1">
                        {filteredAndSortedAssignments.filter(a => ['in-progress', 'in_progress'].includes(a.assignment.status || '')).length}
                    </div>
                </div>

                {/* Bloqué - Highlight if > 0 */}
                {(() => {
                    const blockedCount = filteredAndSortedAssignments.filter(a => a.assignment.status === 'blocked').length;
                    return (
                        <div className={`glass-panel p-3 border-l-4 border-l-red-500 relative overflow-hidden group ${blockedCount > 0 ? 'bg-red-500/5' : ''}`}>
                            <div className="absolute right-2 top-2 opacity-10 group-hover:opacity-20 transition-opacity text-red-500">
                                <Briefcase size={24} />
                            </div>
                            <div className="text-red-400/80 text-[10px] font-bold uppercase tracking-wider">Bloqué</div>
                            <div className="text-2xl font-bold text-red-500 mt-1">{blockedCount}</div>
                        </div>
                    );
                })()}

                {/* Terminé */}
                <div className="glass-panel p-3 border-l-4 border-l-emerald-500 relative overflow-hidden group">
                    <div className="absolute right-2 top-2 opacity-10 group-hover:opacity-20 transition-opacity text-emerald-500">
                        <Briefcase size={24} />
                    </div>
                    <div className="text-emerald-400/80 text-[10px] font-bold uppercase tracking-wider">Terminé</div>
                    <div className="text-2xl font-bold text-emerald-400 mt-1">
                        {filteredAndSortedAssignments.filter(a => ['done', 'completed-by-tech'].includes(a.assignment.status || '')).length}
                    </div>
                </div>
            </div>

            {/* Quick Status Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar">
                <button
                    onClick={() => setSelectedStatus('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${selectedStatus === 'all'
                        ? 'bg-white text-black border-white'
                        : 'bg-[var(--button-bg)] text-muted border-[var(--button-border)] hover:bg-[var(--button-hover-bg)] hover:text-main'
                        }`}
                >
                    Tout
                </button>
                <button
                    onClick={() => setSelectedStatus('pending')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${selectedStatus === 'pending'
                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                        : 'bg-[var(--button-bg)] text-muted border-[var(--button-border)] hover:bg-[var(--button-hover-bg)] hover:text-main'
                        }`}
                >
                    En attente
                </button>
                <button
                    onClick={() => setSelectedStatus('todo')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${selectedStatus === 'todo'
                        ? 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                        : 'bg-[var(--button-bg)] text-muted border-[var(--button-border)] hover:bg-[var(--button-hover-bg)] hover:text-main'
                        }`}
                >
                    Planifié
                </button>
                <button
                    onClick={() => setSelectedStatus('in-progress')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${selectedStatus === 'in-progress'
                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                        : 'bg-[var(--button-bg)] text-muted border-[var(--button-border)] hover:bg-[var(--button-hover-bg)] hover:text-main'
                        }`}
                >
                    En cours
                </button>
                <button
                    onClick={() => setSelectedStatus('blocked')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${selectedStatus === 'blocked'
                        ? 'bg-red-500/20 text-red-500 border-red-500/50'
                        : 'bg-[var(--button-bg)] text-muted border-[var(--button-border)] hover:bg-[var(--button-hover-bg)] hover:text-main'
                        }`}
                >
                    Bloqué
                </button>
                <button
                    onClick={() => setSelectedStatus('completed-by-tech')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${selectedStatus === 'completed-by-tech'
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                        : 'bg-[var(--button-bg)] text-muted border-[var(--button-border)] hover:bg-[var(--button-hover-bg)] hover:text-main'
                        }`}
                >
                    Techniquement Terminé
                </button>
            </div>

            {/* Assignments List or Kanban */}
            <div className="min-h-[400px]">
                {viewMode === 'list' ? (
                    <>
                        <AssignmentList
                            assignments={paginatedAssignments}
                            onEditAssignment={handleEditAssignment}
                            onStartWorksite={onStartWorksite}
                            onFinishWorksite={onFinishWorksite}
                        />
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </>
                ) : (
                    <AssignmentKanban
                        assignments={filteredAndSortedAssignments}
                        onEditAssignment={handleEditAssignment}
                        onStartWorksite={onStartWorksite}
                        onFinishWorksite={onFinishWorksite}
                    />
                )}
            </div>



            {/* Edit Modal */}
            {editingAssignment && (
                <EditAssignmentModal
                    initialAssignment={editingAssignment.assignment}
                    technicians={technicians.filter(t => !t.archived)}
                    initialTechnicianIds={editingAssignment.techIds}
                    services={[]} // Pass empty services or fetch them if needed, but for now empty is better than crash
                    isOpen={true}
                    onClose={() => setEditingAssignment(null)}

                    onSave={handleSaveEdit}
                    onDelete={handleDeleteAssignment}
                />
            )}
        </div>
    );
};
